export type CampaignDetailPostState = {
  id: string;
  scheduledFor: string;
  status: string;
  approvalStatus?: string | null;
};

export type CampaignDetailStatusCounts = {
  readyCount: number;
  inFlightCount: number;
  needsAttentionCount: number;
};

export type CampaignGeneratedContentCounts = {
  all: number;
  pending: number;
  approved: number;
  denied: number;
};

export type CampaignDetailHeroAsset = {
  signedUrl: string;
  width?: number | null;
  height?: number | null;
};

export type CampaignDetailHeroPost = {
  output?: {
    latestCompleted?: {
      assets?: CampaignDetailHeroAsset[] | null;
    } | null;
  } | null;
};

export type CampaignDetailAudienceSegment = {
  label: string;
  description?: string | null;
};

export function getCampaignDetailStatusCounts(
  posts: CampaignDetailPostState[],
): CampaignDetailStatusCounts {
  const counts = posts.reduce<Record<string, number>>((acc, post) => {
    acc[post.status] = (acc[post.status] ?? 0) + 1;
    return acc;
  }, {});

  return {
    readyCount: counts.generated ?? 0,
    inFlightCount: (counts.generating ?? 0) + (counts.scheduled ?? 0),
    needsAttentionCount: counts.failed ?? 0,
  };
}

export function getNextCampaignDetailPost(
  posts: CampaignDetailPostState[],
  now = new Date(),
): CampaignDetailPostState | null {
  const nowTime = now.getTime();
  let nextPost: CampaignDetailPostState | null = null;
  let nextPostTime = Number.POSITIVE_INFINITY;

  for (const post of posts) {
    const scheduledTime = new Date(post.scheduledFor).getTime();

    if (scheduledTime >= nowTime && scheduledTime < nextPostTime) {
      nextPost = post;
      nextPostTime = scheduledTime;
    }
  }

  return nextPost;
}

export function getCampaignGeneratedContentCounts(
  posts: CampaignDetailPostState[],
): CampaignGeneratedContentCounts {
  return posts.reduce<CampaignGeneratedContentCounts>(
    (acc, post) => {
      if (post.status !== "generated") {
        return acc;
      }

      acc.all += 1;

      if (post.approvalStatus === "approved") {
        acc.approved += 1;
      } else if (post.approvalStatus === "rejected") {
        acc.denied += 1;
      } else {
        acc.pending += 1;
      }

      return acc;
    },
    { all: 0, pending: 0, approved: 0, denied: 0 },
  );
}

export function getCampaignScheduleSummary({
  startDate,
  endDate,
  postCount,
  formatDate,
}: {
  startDate: string | Date;
  endDate: string | Date;
  postCount: number;
  formatDate: (date: Date) => string;
}): string {
  const rangeLabel = `${formatDate(new Date(startDate))} - ${formatDate(new Date(endDate))}`;

  if (postCount === 0) {
    return `${rangeLabel}, no planned posts`;
  }

  return `${rangeLabel}, ${postCount} planned ${postCount === 1 ? "post" : "posts"}`;
}

export function getFirstCampaignHeroAsset(
  posts: CampaignDetailHeroPost[],
): CampaignDetailHeroAsset | null {
  for (const post of posts) {
    const asset = post.output?.latestCompleted?.assets?.find(
      (candidate) => candidate.signedUrl.trim().length > 0,
    );

    if (asset) {
      return asset;
    }
  }

  return null;
}

export function serializeCampaignAudienceSegments(
  segments: CampaignDetailAudienceSegment[],
): string {
  return segments
    .map((segment) => {
      const label = segment.label.trim();
      const description = segment.description?.trim();

      if (!label) {
        return "";
      }

      return description ? `${label} - ${description}` : label;
    })
    .filter(Boolean)
    .join("\n");
}

export function parseCampaignAudienceSegments(value: string): CampaignDetailAudienceSegment[] {
  return value
    .split(/\r?\n/u)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separatorIndex = line.indexOf(" - ");

      if (separatorIndex === -1) {
        return { label: line, description: "" };
      }

      return {
        label: line.slice(0, separatorIndex).trim(),
        description: line.slice(separatorIndex + 3).trim(),
      };
    })
    .filter((segment) => segment.label.length > 0);
}
